import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../supabase";

/**
 * ResetPassword — "Set new password" page reached after clicking the
 * recovery email link. Supabase Auth places the session in the URL hash
 * (type=recovery), and getSession() picks it up automatically via
 * detectSessionInUrl: true.
 *
 * The user must be in a recovery-authenticated session (not a full
 * logged-in session) for updateUser to work. After success, redirect
 * to the sign-in page.
 */
export default function ResetPassword() {
    const { t } = useTranslation();
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    async function handleSubmit(e: FormEvent) {
        e.preventDefault();
        setError("");
        setMessage("");

        if (password.length < 6) {
            setError(t("auth.newPasswordHint"));
            return;
        }
        if (password !== confirmPassword) {
            setError(t("auth.passwordsDoNotMatch"));
            return;
        }

        setLoading(true);
        try {
            // The user arrives here with a recovery session (type=recovery).
            // updateUser works because the recovery token grants a temporary session.
            const { error: err } = await supabase.auth.updateUser({
                password,
            });
            if (err) {
                setError(t("auth.passwordResetError"));
                return;
            }
            setMessage(t("auth.passwordSet"));
            // Sign out the recovery session so the user is redirected to sign-in.
            await supabase.auth.signOut();
        } catch {
            setError(t("auth.passwordResetError"));
        } finally {
            setLoading(false);
        }
    }

    const origin = typeof window !== "undefined" ? window.location.origin : "";

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="mb-8 text-center">
                <h2 className="text-3xl font-extrabold text-black">
                    {t("auth.resetPassword")}
                </h2>
            </div>

            {message ? (
                <div className="w-full max-w-sm mx-auto text-center space-y-6">
                    <p className="text-sm font-bold text-emerald-600">{message}</p>
                    <a
                        href={`${origin}/`}
                        className="inline-block px-6 py-3 bg-accent text-white rounded-xl font-black text-xs uppercase tracking-widest"
                    >
                        {t("auth.signIn")}
                    </a>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="w-full max-w-sm mx-auto space-y-4">
                    <div>
                        <label className="block text-xs font-black uppercase tracking-wider text-muted mb-1">
                            {t("auth.newPassword")}
                        </label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="new-password"
                            required
                            minLength={6}
                            placeholder={t("auth.newPasswordHint")}
                            className="w-full px-4 py-3 rounded-xl bg-white border border-border text-sm text-fg outline-hidden focus:ring-2 focus:ring-accent"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-black uppercase tracking-wider text-muted mb-1">
                            {t("auth.confirmPassword")}
                        </label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            autoComplete="new-password"
                            required
                            minLength={6}
                            placeholder={t("auth.confirmPassword")}
                            className="w-full px-4 py-3 rounded-xl bg-white border border-border text-sm text-fg outline-hidden focus:ring-2 focus:ring-accent"
                        />
                    </div>

                    {error && <p className="text-xs font-bold text-rose-500">{error}</p>}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-accent hover:opacity-90 text-white py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all disabled:opacity-50 cursor-pointer"
                    >
                        {loading ? t("auth.settingPassword") : t("auth.setNewPassword")}
                    </button>
                </form>
            )}
        </div>
    );
}